/** Production Canvas — route entry point
 * (VISTOERA_PRODUCTION_CANVAS_V1_FOUNDATION_AND_INTERACTIVE_SHELL).
 *
 * Loads/saves the graph for one `ContentProject` (spec §1 — Canvas is a
 * projection over that project, not a new resource of its own) and
 * renders `CanvasBoard` inside the `ReactFlowProvider` it needs for
 * `useReactFlow()`. Kept intentionally thin — all interaction logic
 * lives in `CanvasBoard`.
 *
 * Project-switch fencing
 * (VISTOERA_CANVAS_PROJECT_SWITCH_DIVERGENCE_AND_STRUCTURED_ASSET_INGESTION_CONTRACT_P0_V1):
 * late fetch / late autosave from a previous projectId must never
 * overwrite the currently routed project.
 *
 * Stale-error/white-frame fix
 * (VISTOERA_WORKSPACE_TO_CANVAS_TRANSITION_ERROR_STATE_FLASH_WHITE_FRAME_AND_FIRST_PAINT_VISUAL_ATOMICITY_FIX_P0_V1
 * §9-§16 root cause A — "ERROR_STATE_FLASH"): React Router does NOT
 * remount this route element just because the `:projectId` URL param
 * changes (same component type at the same Outlet position) — so
 * `canvas`/`loadError`/`loading` used to persist across a project switch
 * until a `useEffect(..., [projectId])` reset them, which only runs
 * AFTER React has already committed (and the browser may already have
 * PAINTED) one render with the previous project's stale error/canvas
 * paired with the new `projectId`. That one-frame-or-more stale paint is
 * exactly the "闪一下旧 Error 再出现新 Canvas" bug.
 *
 * Fixed at the root by giving the actual stateful logic
 * (`ProductionCanvasPageForProject`) a `key={projectId}` here — React
 * then fully unmounts the previous project's instance (discarding ALL
 * its state atomically, in the same commit as the route param change)
 * and mounts a brand-new instance with fresh `useState` initializers
 * before anything paints. No `useEffect`-timing race is possible because
 * there is no "reset" step at all — old state structurally cannot
 * outlive its own `projectId`. (Not the "render-time `setState` hack"
 * pattern the task spec's §12 warns against — this is a plain
 * declarative remount key, the same idiom React's own docs recommend for
 * "reset all state when a prop changes".) */

import { ReactFlowProvider } from '@xyflow/react'
import CanvasFloatingLayerHost from './CanvasFloatingLayerHost'
import { Button, Result, message } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  createContentProject,
  deleteContentProject,
  duplicateContentProject,
  getProductionCanvas,
  getWorkspaceNodeIconOverrides,
  saveContentProjectDraft,
  saveProductionCanvas,
  type SaasApiError,
} from '../../../api'
import { useWorkspaceState } from '../../WorkspaceContext'
import CanvasBoard from './CanvasBoard'
import CanvasErrorBoundary from './CanvasErrorBoundary'
import { markCanvasBootstrap } from './canvasBootstrapTelemetry'
import { classifyCanvasLoadFailure, logCanvasLoadFailure, resolveCanvasLoadFailureMessage } from './canvasLoadFailure'
import { ensureWorkspaceAuthMatrixLoaded } from './authMatrixRuntime'
import { ensureWorkspaceNodeCatalogLoaded } from './nodeCatalogRuntime'
import { refreshWorkspaceQuickActions, refreshWorkspaceQuickActionsIfStale } from './canvasQuickActionRuntime'
import type { CanvasEdgeDto, CanvasNodeDto, CanvasViewportDto, ProductionCanvasDto } from './types'
import { useAuth } from '../../../AuthContext'
import { recordCanvasProjectViewed } from '../../../navigation/personalCanvasRecord'
import { resolveCanvasLoadNotFoundFallback } from '../../../navigation/canvasVLogoNavigation'
import { useCanvasWorkspaceTransition } from '../../canvasWorkspaceTransition'
import { shouldShowCanvasLoadErrorSurface } from './canvasLoadRenderState'
import './production-canvas.css'

function loadNodeIconOverrides(workspaceId: string): void {
  if (!workspaceId) return
  // Legacy endpoint is gap-fill only. Catalog icon_ref wins after
  // ensureWorkspaceNodeCatalogLoaded — never let this overwrite catalog.
  void getWorkspaceNodeIconOverrides(workspaceId)
    .then(async (res) => {
      const { getCachedNodeCatalogEntries, isNodeCatalogLoaded } = await import('./nodeCatalogRuntime')
      const { mergeNodeIconOverrides } = await import('./nodeRegistry')
      const legacy = res.data.overrides || {}
      if (!isNodeCatalogLoaded() || getCachedNodeCatalogEntries().length === 0) {
        mergeNodeIconOverrides(legacy)
        return
      }
      // Only fill types NOT present in tenant catalog assignments.
      const catalogTypes = new Set(getCachedNodeCatalogEntries().map((e) => e.node_type))
      const gap: Record<string, string> = {}
      for (const [nt, icon] of Object.entries(legacy)) {
        if (!catalogTypes.has(nt)) gap[nt] = icon
      }
      if (Object.keys(gap).length) mergeNodeIconOverrides(gap)
    })
    .catch(() => {
      // Best-effort — Canvas falls back to catalog / code-owned defaults.
    })
}

function loadNodeCatalog(workspaceId: string, userId?: string | null): void {
  if (!workspaceId) return
  void ensureWorkspaceNodeCatalogLoaded(workspaceId, userId)
  // VistoeraTASK-00634 — batch effective auth once per Canvas session (same
  // identity key as node catalog). Create surfaces stay empty until ready.
  void ensureWorkspaceAuthMatrixLoaded(workspaceId, userId)
}

/** Gate L (VISTOERA_CANVAS_CANONICAL_HOVER_QUICK_ACTION_ADMIN_DRIVEN_
 * NEXT_NODE_CONTEXT_HANDOFF_P0_V1) — ONE shared config fetch per Canvas
 * entry, never per-node/per-hover. TASK-00584: it is re-fetched on every
 * Canvas entry (so a Platform Admin toggle shows up on re-entering or
 * refreshing the page) and, for a long-lived tab, on re-focus once the
 * snapshot is older than the minimum interval — no polling/websocket. */
function loadQuickActionConfig(workspaceId: string, userId?: string | null): void {
  if (!workspaceId) return
  void refreshWorkspaceQuickActions(workspaceId, userId)
}

function createPlaceholderCanvas(projectId: string): ProductionCanvasDto {
  return {
    id: '',
    project_id: projectId,
    viewport: { x: 0, y: 0, zoom: 1 },
    nodes: [],
    edges: [],
    updated_at: null,
  }
}

/** The actual stateful Canvas-load logic — see the file-header comment
 * for why the default export below mounts this keyed by `projectId`
 * rather than calling it directly. Every hook in here may assume
 * `projectId` is constant for its entire lifetime (a change means a
 * fresh instance, never a re-render of this one). */
function ProductionCanvasPageForProject() {
  const { t } = useTranslation(['canvas', 'contentProjects'])
  const { workspaceId = '', projectId = '' } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { me } = useAuth()
  const initialFocusNodeId = searchParams.get('focusNode')

  const [canvas, setCanvas] = useState<ProductionCanvasDto | null>(null)
  const [projectTitle, setProjectTitle] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const { primarySite } = useWorkspaceState()
  const siteId = primarySite?.id ?? null
  // VISTOERA_WORKSPACE_TO_CANVAS_TRANSITION_ERROR_STATE_FLASH_WHITE_FRAME_AND_FIRST_PAINT_VISUAL_ATOMICITY_FIX_P0_V1
  // §5/§17/§67 — while a Canvas→Workspace Exit is in flight (the V-logo
  // click has already fired but the real `navigate()` is still ~260ms
  // away, per `canvasExitTransitionController`'s deliberate route-handoff
  // delay), this SAME `ProductionCanvasPageForProject` instance is still
  // mounted and still reactive. If its in-flight `load()` rejects during
  // that exact window, it must NOT swap from `CanvasBoard` to the bare
  // error `Result` — the user already chose to leave; interrupting the
  // outgoing slide with a stale-looking Error Surface for its last few
  // frames is the "ERROR SCREEN FLASH → CANVAS/WORKSPACE" bug. Gating the
  // error branch on `phase === 'idle'` below is a read-only render
  // condition (never a `setState` call) — not the render-time `setError`
  // side-effect spec §12 forbids.
  const { phase } = useCanvasWorkspaceTransition()

  /** Retry-fencing identity: bumped on every `load()` call (mount AND
   * manual Retry) so an earlier, slower call can never clobber a later
   * one's result — see `load()` below. Project-switch staleness is
   * handled structurally by the `key={projectId}` remount in the default
   * export, not by this ref (each project gets a fresh instance, so
   * there is no "previous project" epoch to compare against here). */
  const projectEpochRef = useRef(0)
  const activeProjectIdRef = useRef(projectId)
  activeProjectIdRef.current = projectId
  /** VistoeraTASK-00576 - `${userId}::${workspaceId}` the page currently renders for.
   * A Canvas response is applied only if the identity it was requested under is
   * still the active one (old account's late response must never repaint). */
  const activeIdentityRef = useRef('')
  activeIdentityRef.current = `${me?.user.id ?? ''}::${workspaceId}`

  const placeholderCanvas = useMemo(() => createPlaceholderCanvas(projectId), [projectId])
  // TASK-00556 — the server decides; a placeholder / older backend is EDITABLE.
  const readOnly = canvas?.access_mode === 'READ_ONLY'
  const readOnlyRef = useRef(readOnly)
  readOnlyRef.current = readOnly

  useEffect(() => {
    markCanvasBootstrap('CANVAS_ROUTE_MOUNT_AT')
  }, [])

  // VistoeraTASK-00577 — "my canvas" bookkeeping, written ONLY once the server has
  // answered for this exact user + workspace + project, and ONLY for a project this
  // account OWNS (EDITABLE: the server grants edit to the creator, or to the Owner for
  // unattributed legacy rows — never for a teammate's shared / member project):
  //  - Canvas Return Context (refresh-safe, URL `projectId` is the canonical relation),
  //  - VistoeraTASK-00581/00576 last-opened hint (per user + workspace).
  // A READ_ONLY project being viewed (team share / member project) is recorded as
  // "not mine": it never becomes the return context nor the personal recovery target,
  // and a stale hint/context naming it (older builds) is dropped.
  useEffect(() => {
    if (!workspaceId || !projectId || !me?.user.id || !canvas) return
    if (canvas.project_id !== projectId) return
    recordCanvasProjectViewed({ workspaceId, projectId, userId: me.user.id, readOnly })
  }, [workspaceId, projectId, me?.user.id, canvas, readOnly])

  const load = useCallback(async () => {
    if (!workspaceId || !projectId) return
    projectEpochRef.current += 1
    const epoch = projectEpochRef.current
    const requestedProjectId = projectId
    const requestedIdentity = activeIdentityRef.current
    setLoading(true)
    setLoadError(null)
    markCanvasBootstrap('CANVAS_API_REQUEST_START_AT')
    const loadStartedAt = performance.now()
    try {
      const { data } = await getProductionCanvas(workspaceId, requestedProjectId)
      // Stale-fetch fence: ignore responses that no longer match route / identity.
      if (
        epoch !== projectEpochRef.current ||
        activeProjectIdRef.current !== requestedProjectId ||
        activeIdentityRef.current !== requestedIdentity ||
        (data.project_id && data.project_id !== requestedProjectId)
      ) {
        return
      }
      markCanvasBootstrap('CANVAS_API_RESPONSE_AT')
      setCanvas(data)
      setProjectTitle(data.project_title || '')
    } catch (e) {
      if (
        epoch !== projectEpochRef.current ||
        activeProjectIdRef.current !== requestedProjectId ||
        activeIdentityRef.current !== requestedIdentity
      ) {
        return
      }
      logCanvasLoadFailure(e, {
        workspaceId,
        projectId: requestedProjectId,
        loadStage: 'api',
        durationMs: Math.round(performance.now() - loadStartedAt),
      })
      if (classifyCanvasLoadFailure(e) === 'CANVAS_NOT_FOUND') {
        message.warning(t('contentProjects:team.canvas.accessLost'))
        const fallback = resolveCanvasLoadNotFoundFallback(workspaceId, location.pathname)
        navigate(fallback.route, { replace: true })
        return
      }
      const resolved = resolveCanvasLoadFailureMessage(e, t)
      setLoadError(resolved.message || (e as SaasApiError).message || t('page.loadFailed'))
    } finally {
      if (
        epoch === projectEpochRef.current &&
        activeProjectIdRef.current === requestedProjectId &&
        activeIdentityRef.current === requestedIdentity
      ) {
        setLoading(false)
      }
    }
  }, [workspaceId, projectId, t, location.pathname, navigate])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (workspaceId) {
      loadNodeIconOverrides(workspaceId)
      // VistoeraTASK-00583 — catalog cache keyed by user + workspace.
      loadNodeCatalog(workspaceId, me?.user.id)
      loadQuickActionConfig(workspaceId, me?.user.id)
    }
  }, [workspaceId, me?.user.id])

  useEffect(() => {
    if (!workspaceId) return
    const userId = me?.user.id
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshWorkspaceQuickActionsIfStale(workspaceId, userId)
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [workspaceId, me?.user.id])

  const handleSave = useCallback(
    async (payload: { nodes: CanvasNodeDto[]; edges: CanvasEdgeDto[]; viewport: CanvasViewportDto; projectId?: string }) => {
      if (!workspaceId || !projectId || loading || readOnlyRef.current) return
      // Autosave fence: payload may carry the projectId it was scheduled
      // for; reject cross-project writes.
      if (payload.projectId && payload.projectId !== projectId) return
      if (activeProjectIdRef.current !== projectId) return
      await saveProductionCanvas(workspaceId, projectId, {
        nodes: payload.nodes,
        edges: payload.edges,
        viewport: payload.viewport,
      })
    },
    [workspaceId, projectId, loading],
  )


  const handleRenameProject = useCallback(
    async (title: string) => {
      if (readOnlyRef.current) return
      const { data } = await saveContentProjectDraft(workspaceId, projectId, { title })
      setProjectTitle(data.title)
    },
    [workspaceId, projectId],
  )

  const handleDuplicateProject = useCallback(async () => {
    const { data } = await duplicateContentProject(workspaceId, projectId)
    if (readOnlyRef.current) {
      // Copy of someone else's project: private, independent, opens EDITABLE.
      const restricted = data.copy_report?.restricted_asset_count ?? 0
      message.success(
        restricted > 0
          ? t('contentProjects:team.toast.copiedRestricted', { count: restricted })
          : t('contentProjects:team.canvas.copyDone'),
      )
    }
    navigate(`/workspace/${workspaceId}/content-production/${data.id}/canvas`)
  }, [workspaceId, projectId, navigate, t])

  /** VistoeraTASK-00250 §六/§七 — "新建项目" from the Canvas header's
   * project-action menu. Reuses the EXACT canonical create call
   * `ContentProductionListPage.tsx`'s own "新建内容项目" button already
   * uses (`createContentProject` with the same auto-name default,
   * `contentProjects:unnamedProject` — never a bespoke naming rule for
   * this entry point) and never mutates the currently-open project: the
   * new project id is a fresh server-issued id, and navigating to its
   * own Canvas route unmounts THIS instance (`key={projectId}` below)
   * without ever calling any save/duplicate against the current one. On
   * failure this rejects and the caller (`CanvasTopBar`) stays on the
   * current Canvas + shows the existing unified error toast — no
   * partial/half-created project is left behind client-side since the
   * navigate() below never runs unless `createContentProject` already
   * resolved with a real id. */
  const handleCreateProject = useCallback(async () => {
    if (readOnlyRef.current) return
    const { data } = await createContentProject(workspaceId, t('contentProjects:unnamedProject'))
    if (!data?.id) {
      throw new Error(t('contentProjects:toast.createMissingId'))
    }
    navigate(`/workspace/${workspaceId}/content-production/${data.id}/canvas`)
  }, [workspaceId, navigate, t])

  /** VistoeraTASK-00250 §十四/§十五/§十六 — "删除当前项目" from the Canvas
   * header's project-action menu. Reuses the EXACT canonical hard-delete
   * call the Project List page's per-card delete already uses
   * (`deleteContentProject` — real tenant/workspace authorization +
   * ownership validation happens server-side, see
   * `content_production_service.get_owned_project`/`delete_project`).
   * Immediately navigates to `PROJECT_LIST_ROUTE` on success — never
   * stays on `/canvas/{deletedProjectId}` for even one more render.
   * That navigation unmounts this `ProductionCanvasPageForProject`
   * instance (route no longer matches this component at all, not just a
   * `key` change). Debounced autosave flushes on leave (VistoeraTASK-00585 —
   * see `CanvasBoard` / `DebouncedSaveController.dispose`); generation-status
   * polling `setInterval` is cleared by its own effect cleanup. */
  const handleDeleteProject = useCallback(async () => {
    if (readOnlyRef.current) return
    await deleteContentProject(workspaceId, projectId)
    navigate(`/workspace/${workspaceId}/content-production`)
  }, [workspaceId, projectId, navigate])

  if (shouldShowCanvasLoadErrorSurface({ loading, loadError, canvas, transitionPhase: phase })) {
    return (
      <Result
        status="error"
        title={t('page.loadFailed')}
        subTitle={loadError || t('page.unknownError')}
        extra={[
          <Button key="retry" type="primary" onClick={() => void load()}>
            {t('common.retry')}
          </Button>,
          <Button key="back" onClick={() => navigate(`/workspace/${workspaceId}/content-production`)}>
            {t('page.backToProjects')}
          </Button>,
        ]}
      />
    )
  }

  return (
    <ReactFlowProvider>
      <CanvasErrorBoundary>
        {/* key=projectId remounts the board so RF nodes/edges/selection
         * never leak across projects. */}
        <CanvasFloatingLayerHost>
          <CanvasBoard
          key={projectId}
          canvas={canvas ?? placeholderCanvas}
          canvasHydrating={loading}
          workspaceId={workspaceId}
          projectId={projectId}
          projectTitle={projectTitle}
          initialFocusNodeId={initialFocusNodeId}
          onRenameProject={handleRenameProject}
          onDuplicateProject={handleDuplicateProject}
          onCreateProject={handleCreateProject}
          onDeleteProject={handleDeleteProject}
          siteId={siteId}
          onSave={handleSave}
          readOnly={readOnly}
          readOnlyCreatorName={readOnly ? (canvas?.creator?.display_name ?? null) : null}
        />
        </CanvasFloatingLayerHost>
      </CanvasErrorBoundary>
    </ReactFlowProvider>
  )
}

/** Route entry point — see the file-header comment
 * (VISTOERA_WORKSPACE_TO_CANVAS_TRANSITION_ERROR_STATE_FLASH_WHITE_FRAME_AND_FIRST_PAINT_VISUAL_ATOMICITY_FIX_P0_V1)
 * for why `key={projectId}` here (not inside
 * `ProductionCanvasPageForProject` itself) is the actual fix for the
 * stale-error-flash bug: it forces React to fully unmount+remount the
 * stateful component on every project switch, so `canvas`/`loadError`/
 * `loading` can never be read across a project boundary — not even for
 * one render. */
export default function ProductionCanvasPage() {
  const { workspaceId = '', projectId = '' } = useParams()
  const { me } = useAuth()
  // VistoeraTASK-00576: identity + workspace are part of the instance key, so a
  // change of account / workspace remounts the Canvas (React Flow state, local
  // autosave controller, in-flight loads) instead of repainting old data.
  return <ProductionCanvasPageForProject key={`${me?.user.id ?? ''}::${workspaceId}::${projectId}`} />
}
